/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import TextLinkButton from '../../../components/shared/TextLinkButton'
import FolderAndDocumentDetailsModal from '../../../components/modals/FolderAndDocumentDetailsModal'
import { useServiceContext } from '../../../root'
import { useModal } from '../../../contexts/ModalContext'

interface QualityIssuesPopoverProps {
  fsiId: number
  isSuperseded?: boolean
}

/**
 * QualityIssuesPopover component displays a warning indicator with a popover
 * that links to document details when a document has quality issues (documentDefective).
 *
 * This is the TypeScript/React 18 version migrated from the legacy
 * lego/page/browser/list/quality-issues/QualityIssuesPopover.jsx
 */
const QualityIssuesPopover: React.FC<QualityIssuesPopoverProps> = ({ fsiId, isSuperseded = false }) => {
  const context = useServiceContext()
  const { showModal } = useModal()
  const { translate } = context.service.filter

  // Using 'any' type as fileDetails comes from API and is passed directly to FolderAndDocumentDetailsModal
  const [fileDetails, setFileDetails] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const getDocumentDetailsInfo = async () => {
      try {
        const [details, pathInfo] = await Promise.all([
          context.api.files.getDetails(fsiId),
          context.api.files.getFsiPath(fsiId, false)
        ])

        const path = [...pathInfo]
        const lastPathItem = path.pop()

        const newDetails = {
          ...details,
          ...lastPathItem,
          path: path,
          folderPathText:
            path.reduce((memo: string, item: { name: string }) => {
              return (memo ? memo + '/' : '') + item.name
            }, '') +
            '/' +
            details.name
        }
        setFileDetails(newDetails)
      } catch (error) {
        console.error('Failed to fetch document details:', error)
      } finally {
        setIsLoading(false)
      }
    }

    getDocumentDetailsInfo()
  }, [fsiId, context.api.files])

  const handleOpenDocumentDetails = () => {
    if (fileDetails) {
      showModal(<FolderAndDocumentDetailsModal fileDetails={fileDetails} versionInfo={{}} />)
    }
  }

  const popover = (
    <Popover id={`quality-issues-popover-${fsiId}`} className="popover-container">
      <div className="d-flex">
        <i className="ai-icon-purple-gradient pr15"></i>
        {isLoading ? (
          <span>{translate('LOADING...')}</span>
        ) : (
          <div>
            <span>{translate('DOC_DETAILS_FILE_WARNING_POPOVER_MESSAGE_PREFIX')}</span>
            <TextLinkButton className="pl0 pb0 pr0 pt0" action={handleOpenDocumentDetails}>
              {translate('BROWSER_DOC_DETAILS_H_TITLE')}
            </TextLinkButton>
            <span>{translate('DOC_DETAILS_FILE_WARNING_POPOVER_MESSAGE_SUFFIX')}</span>
          </div>
        )}
      </div>
    </Popover>
  )

  return (
    <OverlayTrigger trigger={['click']} rootClose={true} placement="right" overlay={popover}>
      <span
        className={isSuperseded ? 'warning-file-upload-superseded' : 'warning-file-upload-updated'}
        role="button"
        tabIndex={0}
        aria-label={translate('QUALITY_ISSUES_INDICATOR')}
      ></span>
    </OverlayTrigger>
  )
}

export default QualityIssuesPopover
